import { Button } from '@studio/design-system/button';
import { Calendar } from '@studio/design-system/calendar';
import { Centered } from '@studio/design-system/overlay';
import * as Select from '@studio/design-system/select';
import T from '@studio/design-system/themes';
import { dayjs } from '@studio/js-utils/time';
import { clsx } from 'cn';
import { useMemo, useState } from 'react';

/** Expiration component. */
export const Expiration = ({
  initialExpiration,
  onClose,
  onDone
}: {
  initialExpiration: Date | undefined;
  onClose: () => void;
  onDone: (expiration: Date) => void;
}) => {
  const [date, setDate] = useState<Date | undefined>(initialExpiration);

  const [timeValue, setTimeValue] = useState<string | undefined>(
    initialExpiration ? dayjs(initialExpiration).toString() : undefined
  );

  const timeIntervals = useMemo(() => {
    const selectedDay = dayjs(date);

    const minSelectedDayTime = selectedDay.isSame(dayjs(), 'day')
      ? dayjs()
      : selectedDay.hour(0).minute(0).second(0);

    // Round up to the nearest 15 minutes
    const minutes = minSelectedDayTime.minute();
    const roundedMinutes = 15 * Math.ceil(minutes / 15);

    // Array to store the times
    const times = [];
    const nextDay = minSelectedDayTime.add(1, 'day').startOf('d');

    // Loop for 24 hours
    let loopTime = minSelectedDayTime.minute(roundedMinutes).second(0);

    while (loopTime.isBefore(nextDay)) {
      // 96 intervals of 15 minutes in 24 hours
      times.push(loopTime.clone());
      loopTime = loopTime.add(15, 'minute');
    }

    return times;
  }, [date]);

  return (
    <Centered className='min-h-0 bg-black/25'>
      <div
        className={clsx('rounded-12 bg-white p-16 text-foreground', 'flex flex-col gap-12')}
        data-theme={T.getTheme('light')}
      >
        <Calendar
          onChange={(v) => {
            if (!v || (date && dayjs(v).isSame(date, 'day'))) {
              return;
            }

            setDate(v);
            setTimeValue(undefined);
          }}
          value={date}
        />
        <Select.Root
          key={date?.toString()}
          onValueChange={setTimeValue}
          // open
          value={timeValue}
          variant='light'
        >
          <Select.Trigger className='border-1 border-base-200 text-foreground duration-100 data-[state=open]:border-base-300'>
            <Select.Value placeholder='Select Time' />
          </Select.Trigger>

          <Select.Content
            className='max-h-[250px] overflow-auto bg-white'
            data-theme={T.getTheme('light')}
            position='popper'
            style={{
              width: 'var(--radix-popper-anchor-width)'
            }}
          >
            {timeIntervals.map((time) => (
              <Select.Item
                className='tabular-nums'
                color='normal'
                key={time.toString()}
                value={time.toString()}
              >
                {time.format('hh:mm A')}
              </Select.Item>
            ))}
          </Select.Content>
        </Select.Root>

        <div className='mt-12 flex justify-between'>
          <Button
            onClick={() => {
              onClose();
            }}
            size='lg'
            variant='secondary'
          >
            Cancel
          </Button>
          <Button
            disabled={!date || !timeValue}
            onClick={() => {
              if (timeValue) {
                const expirationDate = new Date(timeValue);
                onDone(expirationDate);
              }
            }}
            size='lg'
          >
            Set Expiration
          </Button>
        </div>
      </div>
    </Centered>
  );
};
